import React from "react";
import { useTranslation } from "react-i18next";
import { AppLayout } from "../components/layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Map, HelpCircle, Settings2, ArrowRight } from "lucide-react";
import { useNavigateWithLock } from "../hooks/useNavigateWithLock";
import { useUserStore } from "../store/userStore";

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const breadcrumbs = [
    { title: t('dashboard.breadcrumb'), isCurrentPage: true }
  ];
  const navigate = useNavigateWithLock();
  const { account } = useUserStore();

  const emailName = account?.email ? account.email.split('@')[0] : '';

  const quickActions = [
    {
      title: t('dashboard.guidesTitle'),
      description: t('dashboard.guidesDescription'),
      icon: Map,
      url: "/guides",
      action: t('dashboard.viewGuides'),
      color: "text-blue-600"
    },
    {
      title: t('dashboard.helpCentersTitle'),
      description: t('dashboard.helpCentersDescription'),
      icon: HelpCircle,
      url: "/help-centers",
      action: t('dashboard.viewHelpCenters'),
      color: "text-purple-600"
    },
    {
      title: t('dashboard.guideBrandingTitle'),
      description: t('dashboard.guideBrandingDescription'),
      icon: Settings2,
      url: "/settings/guides-appearance",
      action: t('dashboard.openSettings'),
      color: "text-gray-600"
    }
  ];

  return (
    <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
      <div className="space-y-6 p-4">
        {/* Welcome Section */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">
            {emailName
              ? t('dashboard.welcome', { name: emailName })
              : t('dashboard.welcomeDefault')}
          </h1>
          <p className="text-muted-foreground">
            {t('dashboard.subtitle')}
          </p>
        </div>

        {/* Quick Actions Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <Card key={action.url} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-muted ${action.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg">{action.title}</CardTitle>
                    </div>
                  </div>
                  <CardDescription className="mt-2">
                    {action.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate(action.url)}
                  >
                    {action.action}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

      </div>
    </AppLayout>
  );
};

export default DashboardPage;
